import type { ReactNode, Ref } from 'react';
import { Button, Select } from '../components';

/** The page frame inside the shell. `narrow` caps the width, for organizer pages and forms. */
export function Page({ children, narrow }: { children: ReactNode; narrow?: boolean }) {
  return <div className={narrow ? 'rq-page is-narrow' : 'rq-page'}>{children}</div>;
}

/** One title per page, with its meta line, tabs and filters, and the one primary action. */
export function PageHeader({
  title,
  titleRef,
  eyebrow,
  meta,
  actions,
  children,
}: {
  title: ReactNode;
  /** Makes the heading focusable by script, for moving focus to it. */
  titleRef?: Ref<HTMLHeadingElement>;
  eyebrow?: ReactNode;
  meta?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <header className="rq-page-head">
      <div className="rq-page-head-row">
        <div className="rq-page-head-text">
          {eyebrow ? <div className="rq-page-eyebrow">{eyebrow}</div> : null}
          <h1
            className="text-title rq-page-title"
            ref={titleRef}
            tabIndex={titleRef ? -1 : undefined}
          >
            {title}
          </h1>
          {meta ? <div className="rq-page-meta">{meta}</div> : null}
        </div>
        {actions ? <div className="rq-page-actions">{actions}</div> : null}
      </div>
      {children}
    </header>
  );
}

/** "1–25 of 312" with previous and next, and optionally the page size. */
export function Pager({
  page,
  pageSize,
  total,
  onPage,
  noun = 'records',
  pageSizes,
  onPageSize,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
  noun?: string;
  pageSizes?: readonly number[];
  onPageSize?: (size: number) => void;
}) {
  if (total === 0) return null;
  const first = (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);
  const pages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <nav className="rq-pager" aria-label="Pages">
      <span className="rq-pager-range" aria-live="polite">
        {first.toLocaleString('en-US')}–{last.toLocaleString('en-US')} of{' '}
        {total.toLocaleString('en-US')} {noun}
      </span>
      {pageSizes && onPageSize ? (
        <Select
          label="Rows per page"
          hideLabel
          size="sm"
          value={String(pageSize)}
          onChange={(e: { target: { value: string } }) => onPageSize(Number(e.target.value))}
          options={pageSizes.map((n) => ({ value: String(n), label: `${n} per page` }))}
        />
      ) : null}
      <Button size="sm" variant="quiet" onClick={() => onPage(page - 1)} disabled={page <= 1}>
        Previous
      </Button>
      <Button size="sm" variant="quiet" onClick={() => onPage(page + 1)} disabled={page >= pages}>
        Next
      </Button>
    </nav>
  );
}
